import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment-timezone';

import { Calendar, Dropdown, Spinner } from '@fluentui/react';
import { Icon } from '@fluentui/react/lib/Icon';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { RadioButton as Availability } from '@bingads-webui-campaign-react/smart-component-primitive';

const getStyles = () => ({
  duration: {
    marginBottom: 30,
  },
  timePicker: ({ isMobile } = {}) => ({
    display: 'flex',
    ...(
      isMobile
        ? {
          flexDirection: 'column',
          alignItems: 'center',
        }
        : {}
    ),
    '& .card th': {
      textAlign: 'center',
    },

    '& .indicator': {
      display: 'flex',
      alignItems: 'center',
      height: 30,
      marginBottom: 8,
      fontWeight: 600,

      '& i': {
        fontWeight: 600,
      },

      '& span': {
        marginLeft: 5,
        marginTop: '-2px',
      },
    },

    '& .time-list': {
      minHeight: 200,
      minWidth: 200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }),
  availability: ({ isMobile } = {}) => ({
    marginLeft: 25,
    marginTop: isMobile ? 13 : 0,
  }),
  error: {
    color: 'red',
    position: 'absolute',
    bottom: 0,
  },
  mobileTimelist: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  timeHint: {
    fontStyle: 'italic',
    fontSize: 12,
  },
});

const calenderStyle = {
  root: {
    boxShadow: '0 3px 10px -5px grey',
  },
};

const availabilityLabelId = 'availability-label';

const UnstyledBookingStepTime = ({
  classes,
  className,
  i18n,
  isLoading,
  availability,
  durationOptions,
  selectedDuration,
  onSelectDuration,
  selectedDate,
  onSelectDate,
  selectedTime,
  onSelectTime,
  errorMessage,
  isMobile,
  timezoneOffset,
  isInPreview,
  focus,
}) => {
  const dropdownRef = useRef();

  useEffect(() => {
    if (dropdownRef.current && focus) {
      // Focus on the first control
      dropdownRef.current.focus();
    }
  }, [focus]);

  return (
    <div className={classNames(className, 'step-time')}>
      <div className={classes.duration}>
        <Dropdown
          componentRef={(input) => { dropdownRef.current = input; }}
          label={i18n.getString(_TL_('Appointment duration'))}
          options={durationOptions}
          selectedKey={selectedDuration.key}
          onChange={onSelectDuration}
        />
      </div>
      {
        isInPreview && (
          <div className={classes.timeHint}>
            *{i18n.getString(_TL_('Your appointment availability will automatically show to users in their local time'))}
          </div>
        )
      }
      <div className={classNames(classes.timePicker, 'time-picker')}>
        <div className={classNames('card', classes.day)}>
          <div className="indicator">
            <Icon iconName="Calendar" />
            <span>{i18n.getString(_TL_('Date'))}</span>
          </div>
          <Calendar
            showMonthPickerAsOverlay
            highlightSelectedMonth
            showGoToToday={false}
            onSelectDate={onSelectDate}
            value={selectedDate}
            calendarDayProps={{
              minDate: moment().add(timezoneOffset - moment().utcOffset(), 'm').toDate(),
              styles: {
                weekRow: 'week-row',
              },
            }}
            styles={calenderStyle}
          />
        </div>
        <div className={classNames('card', classes.availability)}>
          <div className="indicator">
            <Icon iconName="TimePicker" />
            <span id={availabilityLabelId}>{i18n.getString(_TL_('Time'))}</span>
          </div>
          <div className="time-list">
            {
              isLoading
                ? <Spinner label={i18n.getString(_TL_('Loading'))} />
                : <Availability
                  className={classNames({ [classes.mobileTimelist]: isMobile })}
                  emptyLabel={i18n.getString(_TL_('There are no more available times today'))}
                  options={availability}
                  selectedItem={selectedTime}
                  onSelectItem={onSelectTime}
                  ariaDescribedBy={availabilityLabelId}
                />
            }
          </div>
        </div>
      </div>
      {
        errorMessage && (
          <div className={classNames(classes.error, 'step-time-error')}>
            <span>*{errorMessage}</span>
          </div>
        )
      }
    </div>
  );
};

UnstyledBookingStepTime.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isLoading: PropTypes.bool,
  availability: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  })).isRequired,
  durationOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  })).isRequired,
  selectedDuration: PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
  onSelectDuration: PropTypes.func.isRequired,
  selectedDate: PropTypes.shape({}).isRequired,
  onSelectDate: PropTypes.func.isRequired,
  selectedTime: PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
  }).isRequired,
  onSelectTime: PropTypes.func.isRequired,
  errorMessage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({}),
  ]),
  isMobile: PropTypes.bool,
  timezoneOffset: PropTypes.number,
  isInPreview: PropTypes.bool,
  focus: PropTypes.bool,
};

UnstyledBookingStepTime.defaultProps = {
  className: '',
  errorMessage: null,
  isLoading: false,
  isMobile: false,
  timezoneOffset: moment().utcOffset(),
  isInPreview: false,
  focus: false,
};

export const BookingStepTime = React.memo(withDefaultStyles(
  UnstyledBookingStepTime,
  getStyles
));
